import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { BRAND } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll respond within one business day.");
      setForm({ full_name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at 608-513-7639.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#005165] text-[#FFFFFF] relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 80% 30%, #AC9667 0%, transparent 35%)',
      }} aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        {/* Left: Info */}
        <div className="lg:col-span-5">
          <span className="gold-divider mb-5" style={{ color: "#C4B088" }}>Get In Touch</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.08]">
            Let's talk about <em className="not-italic text-[#C4B088]">your family.</em>
          </h2>
          <p className="mt-5 text-[#FFFFFF]/80 text-[17px] leading-relaxed max-w-md">
            Reach out today for a warm, judgment-free conversation. Whether you're just exploring
            or care is needed urgently — we're here.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="contact-phone-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-[#AC9667]/15 border border-[#AC9667]/30 flex items-center justify-center shrink-0 group-hover:bg-[#AC9667] transition-colors">
                <Phone className="w-5 h-5 text-[#C4B088] group-hover:text-white" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#FFFFFF]/60">Call us</p>
                <p className="font-serif text-2xl text-[#FFFFFF] group-hover:text-[#C4B088] transition-colors">{BRAND.phone}</p>
              </div>
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-[#AC9667]/15 border border-[#AC9667]/30 flex items-center justify-center shrink-0 group-hover:bg-[#AC9667] transition-colors">
                <Mail className="w-5 h-5 text-[#C4B088] group-hover:text-white" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#FFFFFF]/60">Email</p>
                <p className="text-[17px] text-[#FFFFFF] group-hover:text-[#C4B088] transition-colors break-all">{BRAND.email}</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-[#AC9667]/15 border border-[#AC9667]/30 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-[#C4B088]" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#FFFFFF]/60">Service Area</p>
                <p className="text-[17px] text-[#FFFFFF]">Madison, Wisconsin & surrounding communities</p>
              </div>
            </div>
          </div>

          {/* Service area map */}
          <div className="mt-8 rounded-2xl overflow-hidden border border-[#FFFFFF]/15 aspect-[4/3]">
            <iframe
              data-testid="service-area-map"
              title="Madison Wisconsin service area"
              src="https://www.google.com/maps?q=Madison%2C+Wisconsin&output=embed"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-[#FFFFFF] rounded-3xl p-8 md:p-10 text-[#005165] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#AC9667]" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#AC9667]">Calm. Confidential. Caring.</span>
            </div>
            <h3 className="font-serif text-3xl text-[#005165] mb-1">Send a Message</h3>
            <p className="text-sm text-[#64748B] mb-7">We typically respond within one business day.</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ct-name">Full Name *</Label>
                <Input id="ct-name" data-testid="ct-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9] focus-visible:ring-[#AC9667]" />
              </div>
              <div>
                <Label htmlFor="ct-email">Email *</Label>
                <Input id="ct-email" data-testid="ct-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9] focus-visible:ring-[#AC9667]" />
              </div>
              <div>
                <Label htmlFor="ct-phone">Phone</Label>
                <Input id="ct-phone" data-testid="ct-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
              </div>
              <div>
                <Label htmlFor="ct-subject">Subject</Label>
                <Input id="ct-subject" data-testid="ct-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ct-message">Message *</Label>
                <Textarea id="ct-message" data-testid="ct-message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="ct-submit"
                type="submit"
                disabled={loading}
                className="bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full h-12 px-8 btn-shimmer"
              >
                {loading ? "Sending..." : (<><Send className="w-4 h-4 mr-2" /> Send Message</>)}
              </Button>
              <Button
                type="button"
                data-testid="ct-start-intake"
                onClick={() => (window.location.href = "/intake")}
                variant="outline"
                className="border-[#005165]/20 text-[#005165] hover:bg-[#005165] hover:text-[#FFFFFF] rounded-full h-12 px-8"
              >
                Start Intake Form Instead
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
